import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Copy, Download, ChevronRight, ChevronDown } from "lucide-react";

// A simple component to display JSON data with collapsible sections and syntax highlighting
function JsonViewer({ data, onDownload }) {
  const [jsonString, setJsonString] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    itemsList: true,
    shop: true,
    totals: true,
  });

  useEffect(() => {
    if (data) {
      setJsonString(JSON.stringify(data, null, 2));
    }
  }, [data]);

  const handleCopy = () => {
    if (jsonString) {
      navigator.clipboard.writeText(jsonString);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 rounded-md border border-dashed border-gray-300">
        <div className="text-center text-gray-500">
          <p>No results yet</p>
          <p className="text-sm mt-1">
            Extract OCR and test a strategy to see results here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="mb-2 flex justify-end space-x-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleCopy}
          title="Copy JSON"
        >
          <Copy className="h-4 w-4 mr-1" />
          Copy
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onDownload}
          title="Download JSON"
        >
          <Download className="h-4 w-4 mr-1" />
          Download
        </Button>
      </div>

      <div className="bg-white rounded-md border overflow-y-auto h-80 p-4">
        <div className="mb-4">
          <div
            className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded"
            onClick={() => toggleSection("shop")}
          >
            {expandedSections.shop ? (
              <ChevronDown className="h-4 w-4 mr-1 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-1 text-gray-500" />
            )}
            <div className="font-medium">Shop Information</div>
          </div>

          {expandedSections.shop && (
            <div className="ml-6 mt-1 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Shop Name:</span>
                <span className="block font-medium">
                  {data.shopName || "N/A"}
                </span>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Invoice #:</span>
                <span className="block font-medium">
                  {data.invoiceNo || "N/A"}
                </span>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Address:</span>
                <span className="block font-medium">
                  {data.shopAddress || "N/A"}
                </span>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Date:</span>
                <span className="block font-medium">
                  {data.purchaseDate || "N/A"}
                </span>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Mobile:</span>
                <span className="block font-medium">
                  {data.shopMobileNumber || "N/A"}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div
            className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded"
            onClick={() => toggleSection("totals")}
          >
            {expandedSections.totals ? (
              <ChevronDown className="h-4 w-4 mr-1 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-1 text-gray-500" />
            )}
            <div className="font-medium">Invoice Totals</div>
          </div>

          {expandedSections.totals && (
            <div className="ml-6 mt-1 grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Sub Total:</span>
                <span className="block font-medium">
                  {data.subTotal ? `₹${data.subTotal.toFixed(2)}` : "N/A"}
                </span>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Total Discount:</span>
                <span className="block font-medium">
                  {data.totalDiscount
                    ? `₹${data.totalDiscount.toFixed(2)}`
                    : "N/A"}
                </span>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <span className="text-gray-500 text-sm">Grand Total:</span>
                <span className="block font-medium text-green-600">
                  {data.grandTotal ? `₹${data.grandTotal.toFixed(2)}` : "N/A"}
                </span>
              </div>
            </div>
          )}
        </div>

        <div>
          <div
            className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded"
            onClick={() => toggleSection("itemsList")}
          >
            {expandedSections.itemsList ? (
              <ChevronDown className="h-4 w-4 mr-1 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-1 text-gray-500" />
            )}
            <div className="font-medium">
              Items ({data.itemsList?.length || 0})
            </div>
          </div>

          {expandedSections.itemsList &&
            data.itemsList &&
            data.itemsList.length > 0 && (
              <div className="ml-6 mt-2 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MRP
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Disc.
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.itemsList.map((item, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2 whitespace-normal text-sm text-gray-900">
                          {item.description}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                          {item.quantity}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                          ₹{item.mrp?.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                          ₹{item.rate?.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-red-500 text-right">
                          {item.discount > 0
                            ? `₹${item.discount.toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          ₹{item.subTotal?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          {expandedSections.itemsList &&
            (!data.itemsList || data.itemsList.length === 0) && (
              <div className="ml-6 mt-1 p-2 text-gray-500 italic">
                No items found
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default JsonViewer;
